import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { paymentService } from '../services';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export default function PaymentRedirect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    const handlePayment = async () => {
      try {
        // Get payment_intent from URL (Stripe redirects with payment_intent, not client_secret)
        const paymentIntentId = searchParams.get('payment_intent');
        const clientSecret = searchParams.get('client_secret');
        const redirectStatus = searchParams.get('redirect_status');
        
        console.log('Payment redirect params:', { 
          paymentIntentId, 
          clientSecret: clientSecret ? 'exists' : 'missing',
          redirectStatus
        });
        
        // Check if payment was successful according to Stripe
        if (redirectStatus !== 'succeeded') {
          console.error('Payment not successful according to Stripe:', redirectStatus);
          setError(t('paymentRedirect.errors.paymentNotSuccessful', { status: redirectStatus || '' }));
          setLoading(false);
          return;
        }

        if (!paymentIntentId) {
          console.error('No payment intent ID in redirect URL');
          setError(t('paymentRedirect.errors.paymentInfoMissing'));
          setLoading(false);
          return;
        }

        // Confirm the payment with our backend
        console.log('Confirming payment with backend:', paymentIntentId);
        const result = await paymentService.confirmPayment(paymentIntentId);
        
        console.log('Payment confirmation result:', result);
        
        if (result.success) {
          toast.success(t('payment.paymentRedirect.paymentSuccessful'));
          
          // Try to extract subscription ID from the payment intent metadata
          // This is a fallback in case it's not in the URL
          let subscriptionId = searchParams.get('subscription_id');
          
          if (!subscriptionId) {
            // If no subscription ID in URL, we need to fetch it from our backend
            try {
              const paymentDetails = await paymentService.getPaymentDetails(paymentIntentId);
              subscriptionId = paymentDetails.subscriptionId;
              console.log('Retrieved subscription ID from payment details:', subscriptionId);
            } catch (err) {
              console.error('Failed to get subscription ID from payment details:', err);
            }
          }
          
          // Redirect to success page
          navigate('/payment-success', { 
            state: { 
              subscriptionId,
              fromRedirect: true,
              paymentIntentId
            } 
          });
        } else {
          console.error('Payment confirmation failed:', result);
          setError(t('paymentRedirect.errors.confirmationFailed'));
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Payment redirect error:', err);
        setError(err.message || t('paymentRedirect.errors.paymentFailed'));
        setLoading(false);
      }
    };

    handlePayment();
  }, [navigate, searchParams, t]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-xl p-8 text-center max-w-md w-full shadow-md">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 size={60} className="text-yellow-500 animate-spin mb-4" />
            <h1 className="text-2xl font-bold mb-2">{t('paymentRedirect.processingPayment')}</h1>
            <p className="text-gray-600">
              {t('paymentRedirect.pleaseWait')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <h1 className="text-2xl font-bold mb-4">{t('paymentRedirect.paymentError')}</h1>
            <p className="text-red-500 mb-6">{error}</p>
            <button
              onClick={() => navigate('/subscription')}
              className="w-full h-[50px] rounded-[16px] font-medium text-black bg-[#F9D348]"
            >
              {t('paymentRedirect.tryAgain')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 